import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const C = {
  navy:    '#0f172a',
  ink:     '#1e293b',
  muted:   '#64748b',
  border:  '#e2e8f0',
  indigo:  '#4f46e5',
  indigoXL:'#eef2ff',
  snow:    '#f8fafc',
  white:   '#ffffff',
  red:     '#dc2626',
};

const Section = ({ id, title, children }) => (
  <section id={id} style={{ paddingBottom: 48, borderBottom: `1px solid ${C.border}`, marginBottom: 48 }}>
    <h2 style={{ fontSize: 'clamp(1.1rem,2vw,1.35rem)', fontWeight: 800, color: C.navy, margin: '0 0 16px', letterSpacing: '-0.02em' }}>{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p style={{ fontSize: '0.92rem', color: C.muted, lineHeight: 1.85, margin: '0 0 14px' }}>{children}</p>
);

const Ul = ({ items }) => (
  <ul style={{ margin: '0 0 14px', paddingLeft: 0, listStyle: 'none' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '5px 0', fontSize: '0.9rem', color: C.muted, lineHeight: 1.7 }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.indigo, marginTop: 9, flexShrink: 0 }} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Warning = ({ children }) => (
  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
    <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: 1 }}>⚠</span>
    <div style={{ fontSize: '0.88rem', color: '#7f1d1d', lineHeight: 1.75 }}>{children}</div>
  </div>
);

const Highlight = ({ label, children }) => (
  <div style={{ background: C.indigoXL, border: '1px solid #c7d2fe', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
    {label && <div style={{ fontSize: '0.62rem', fontWeight: 800, color: C.indigo, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 6 }}>{label}</div>}
    <div style={{ fontSize: '0.88rem', color: C.ink, lineHeight: 1.75 }}>{children}</div>
  </div>
);

const LegalFooter = () => (
  <footer style={{ background: C.snow, padding: '24px clamp(20px,5vw,60px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, borderTop: `1px solid ${C.border}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <img src="/logo192.png" alt="AssessMonk" style={{ width: 22, height: 22, borderRadius: 6, objectFit: 'contain' }} />
      <span style={{ fontWeight: 800, fontSize: '0.82rem', color: C.navy }}>AssessMonk</span>
    </div>
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
      <Link to="/privacy"    style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</Link>
      <Link to="/terms"      style={{ fontSize: '0.72rem', color: C.indigo, textDecoration: 'none', fontWeight: 700 }}>Terms</Link>
      <Link to="/disclaimer" style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Disclaimer</Link>
      <Link to="/refund"     style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Refund Policy</Link>
      <Link to="/contact"    style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Contact</Link>
    </div>
    <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>© 2026 AssessMonk. All rights reserved.</div>
  </footer>
);

export default function TermsAndConditions() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: C.white, color: C.navy, minHeight: '100vh' }}>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.border}`, padding: '0 clamp(20px,5vw,64px)', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 100 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: C.navy }}>AssessMonk</span>
        </Link>
        <button onClick={() => navigate('/app')} style={{ padding: '7px 18px', borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', background: C.indigo, color: '#fff', border: 'none', cursor: 'pointer' }}>
          Try AI Assessment
        </button>
      </nav>

      {/* Hero */}
      <div style={{ background: `linear-gradient(to bottom, ${C.indigoXL}, ${C.white})`, padding: 'clamp(48px,8vw,80px) clamp(20px,5vw,64px) 0' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.indigoXL, border: '1px solid #c7d2fe', borderRadius: 99, padding: '5px 14px', fontSize: '0.7rem', fontWeight: 700, color: C.indigo, marginBottom: 20, letterSpacing: '0.03em' }}>
            Legal · Terms
          </div>
          <h1 style={{ fontSize: 'clamp(1.9rem,4vw,2.8rem)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.03em', margin: '0 0 16px' }}>
            Terms &amp; Conditions
          </h1>
          <p style={{ fontSize: '1rem', color: C.muted, lineHeight: 1.75, maxWidth: 600, margin: '0 0 24px' }}>
            Please read these terms carefully before using AssessMonk. By accessing or using the platform, you agree to be bound by these terms.
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: C.muted, fontWeight: 600, background: C.snow, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 14px', marginBottom: 48 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
            Last updated: 25 April 2026
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '48px clamp(20px,5vw,64px) 80px' }}>

        <Warning>
          AssessMonk is an educational and clinical support platform. It does not constitute medical advice, clinical diagnosis, or a replacement for direct professional assessment of real patients. All AI-generated outputs are for educational reference only. For the full clinical disclaimer, see our <Link to="/disclaimer" style={{ color: C.red, fontWeight: 700 }}>Disclaimer page</Link>.
        </Warning>

        <Section id="acceptance" title="1. Acceptance of Terms">
          <P>By creating an account or using AssessMonk in any way, you confirm that you have read, understood, and agree to these Terms and Conditions in full. If you do not agree, you must not use the platform.</P>
          <P>These Terms apply to all users including physiotherapy students, registered physiotherapists, physiotherapy educators, and any other healthcare professionals accessing the platform.</P>
          <P>We reserve the right to update these Terms at any time. Continued use of the platform after changes are posted constitutes acceptance of the revised Terms.</P>
        </Section>

        <Section id="eligibility" title="2. Eligibility & Age Requirements">
          <P>To use AssessMonk, you must:</P>
          <Ul items={[
            'Be at least 18 years of age; or',
            'If under 18, have explicit consent from a parent or legal guardian who agrees to these Terms on your behalf and accepts responsibility for your use of the platform.',
            'Have the legal capacity to enter into a binding agreement in your jurisdiction.',
          ]} />
          <P>By creating an account, you confirm that you meet these eligibility requirements. AssessMonk reserves the right to request verification of age and to suspend accounts that do not meet these requirements.</P>
          <Highlight label="Note for institutions">
            If AssessMonk is being used within a teaching institution for students under 18, the institution is responsible for obtaining appropriate consent from students and/or guardians and ensuring the platform is used in compliance with applicable data protection requirements.
          </Highlight>
        </Section>

        <Section id="platform-description" title="3. Platform Description & Educational Purpose">
          <P>AssessMonk provides structured clinical assessment frameworks across three specialty modules: Orthopaedic, Neurological, and Cardiopulmonary physiotherapy.</P>
          <P>The platform is designed to:</P>
          <Ul items={[
            'Teach structured clinical reasoning through guided assessment frameworks.',
            'Provide AI-assisted interpretation of clinician-entered findings for educational reference.',
            'Offer structured guides covering clinical assessment theory, special tests, and management principles.',
            'Support physiotherapy students and practitioners in developing systematic assessment skills.',
          ]} />
          <Highlight label="Educational use">
            AssessMonk is primarily an educational tool. Outputs generated by the platform are designed to support clinical reasoning education, not to replace clinical judgment or professional clinical assessment of real patients.
          </Highlight>
        </Section>

        <Section id="no-medical-advice" title="4. Not a Medical Device — Clinical Disclaimer">
          <Warning>
            AssessMonk is not a registered medical device. AI-generated clinical outputs, differential diagnoses, treatment plans, and management recommendations produced by the platform are for educational and professional development purposes only. They must not be used as the sole basis for clinical decisions affecting real patients.
          </Warning>
          <P>Specifically, users acknowledge and agree that:</P>
          <Ul items={[
            'No AI-generated output constitutes a confirmed medical diagnosis.',
            'AI-generated treatment plans are educational templates and require professional clinical judgment to adapt for real patient care.',
            'The platform does not have access to real patient imaging, laboratory results, or direct clinical examination data.',
            'AssessMonk outputs do not replace assessment, diagnosis, or management decisions made by a qualified, registered physiotherapist.',
            'Users in clinical practice remain solely responsible for all clinical decisions made in relation to their patients.',
          ]} />
          <P>If you are using the platform in a clinical education context, all AI outputs must be reviewed, validated, and contextualised by a supervising qualified clinician before application to any real patient scenario.</P>
        </Section>

        <Section id="professional-responsibility" title="5. User Responsibility & Professional Standards">
          <P>All users of AssessMonk who hold or are training toward a regulated healthcare qualification remain bound by the professional and ethical standards of their governing body (e.g., the relevant Physiotherapy Board or Council in their jurisdiction).</P>
          <P>By using AssessMonk, you agree that:</P>
          <Ul items={[
            'You will use the platform in accordance with your professional obligations.',
            'You will not input identifiable real patient health information without appropriate consent and data governance procedures in place.',
            'You will not represent AI-generated outputs as your own independent clinical assessment in any professional, academic, or clinical documentation.',
            'You take sole responsibility for how AI-generated content is interpreted and applied.',
            'You will not use AssessMonk as a substitute for continuing professional development, clinical supervision, or regulated clinical training.',
          ]} />
        </Section>

        <Section id="academic-integrity" title="6. Academic Integrity">
          <Warning>
            Misuse of AI-generated content for academic misconduct is strictly prohibited and may result in immediate account suspension.
          </Warning>
          <P>AssessMonk is designed to support learning — not to replace it. By using this platform, you explicitly agree that you will not:</P>
          <Ul items={[
            'Submit AI-generated clinical outputs as your own original work in any assessed academic context, including essays, case studies, OSCEs, viva submissions, or clinical placement portfolios.',
            'Use AI-generated content to engage in plagiarism, exam fraud, assignment cheating, or any form of academic dishonesty as defined by your institution.',
            'Present AI outputs as independently produced clinical reasoning in any formal examination or certification context.',
            'Use the platform to generate answers to set examination questions or structured assessment tasks where independent work is required.',
            'Produce false or misleading clinical documentation for professional records, legal proceedings, or regulatory submissions based on AI outputs.',
          ]} />
          <P>AssessMonk is not responsible for academic misconduct outcomes arising from misuse of the platform. Users who misuse AI outputs in assessed or professional contexts do so entirely at their own risk and bear sole responsibility for any consequences.</P>
        </Section>

        <Section id="user-accounts" title="7. User Accounts">
          <P>To use AssessMonk's assessment tools, you must create an account using Google OAuth. By creating an account, you agree to:</P>
          <Ul items={[
            'Provide accurate and truthful information.',
            'Keep your account credentials secure.',
            'Not share your account with other individuals.',
            'Notify us immediately of any unauthorised access to your account.',
          ]} />
          <P>We reserve the right to suspend or terminate accounts that violate these Terms, misuse the platform, or pose a risk to the integrity of the service.</P>
        </Section>

        <Section id="acceptable-use" title="8. Acceptable Use">
          <P>You agree not to use AssessMonk to:</P>
          <Ul items={[
            'Input identifiable real patient data without appropriate governance and consent.',
            'Attempt to reverse-engineer, scrape, or extract proprietary clinical reasoning logic or AI prompt structures.',
            'Use automated tools to access, crawl, or extract data from the platform.',
            'Misrepresent AI-generated outputs as your own original clinical work in any assessed or professional context.',
            'Attempt to circumvent authentication, security features, or access controls.',
            'Use the platform for any unlawful purpose.',
            'Harass, harm, or discriminate against any person in connection with your use of the platform.',
          ]} />
        </Section>

        <Section id="service-modifications" title="9. Platform Changes & Service Availability">
          <P>AssessMonk is a continuously evolving platform. We reserve the right to:</P>
          <Ul items={[
            'Update, modify, improve, or remove any feature or module of the platform at any time, with or without prior notice.',
            'Change the platform\'s user interface, assessment frameworks, AI models, or clinical content.',
            'Introduce, modify, or discontinue pricing tiers and subscription plans.',
            'Temporarily suspend access for maintenance, upgrades, or emergency technical reasons.',
            'Permanently discontinue the platform or specific features if required.',
          ]} />
          <P>Where changes materially affect paid users or result in permanent removal of a paid feature, AssessMonk will provide reasonable notice where practicable. We are not liable for any inconvenience, data inaccessibility, or loss arising from platform modifications or downtime.</P>
          <Highlight label="Early access">
            During the current early access period, the platform is in active development. Features may change, be added, or be removed as the product matures. Early access users accept this as part of using a product that is still being built.
          </Highlight>
        </Section>

        <Section id="intellectual-property" title="10. Intellectual Property">
          <P>All content, frameworks, design, code, clinical logic structures, prompt architectures, and materials on the AssessMonk platform are the intellectual property of AssessMonk and are protected under applicable copyright and intellectual property law.</P>
          <P>You may not:</P>
          <Ul items={[
            'Copy, reproduce, or redistribute AssessMonk\'s clinical frameworks, assessment logic, or prompt structures.',
            'Use AssessMonk\'s brand, name, or materials without prior written consent.',
            'Build derivative products or services based on AssessMonk\'s platform logic or structure.',
          ]} />
          <P>Assessment records you create and save within the platform are your own. You retain ownership of the clinical data you input. AssessMonk does not claim ownership of your entered data.</P>
        </Section>

        <Section id="payments" title="11. Payments, Subscriptions & Refunds">
          <P>AssessMonk currently provides free access to core platform features during the early access period. Future pricing, subscription tiers, or premium features may be introduced with advance notice to users.</P>
          <P>When paid plans are introduced:</P>
          <Ul items={[
            'Billing will be clearly communicated and confirmed before any charge is applied.',
            'Subscriptions are billed on a monthly or annual cycle, as selected at the point of purchase.',
            'Subscription cancellation is available at any time; access continues until the end of the current billing period.',
            'Refund eligibility is subject to our Refund Policy — see the full policy at assessmonk.com/refund.',
            'Prices are listed exclusive of applicable taxes (including GST), which will be itemised at checkout.',
            'Users will receive a minimum of 14 days\' notice before any new charges are introduced.',
          ]} />
          <P>For data deletion requests, account closure, and associated data handling, please refer to our <Link to="/privacy" style={{ color: C.indigo, fontWeight: 600 }}>Privacy Policy</Link>.</P>
        </Section>

        <Section id="disclaimers" title="12. Disclaimers & Limitation of Liability">
          <P>AssessMonk is provided on an "as is" and "as available" basis. We make no warranties, express or implied, about the accuracy, reliability, or completeness of AI-generated clinical content.</P>
          <P>To the fullest extent permitted by applicable law, AssessMonk shall not be liable for:</P>
          <Ul items={[
            'Any clinical outcome, patient harm, or professional consequence arising from reliance on AI-generated outputs.',
            'Loss of data due to technical failure, user error, or third-party service outages.',
            'Indirect, incidental, or consequential damages of any kind.',
            'Platform downtime, errors, or service interruptions.',
            'Consequences arising from platform modifications, feature removal, or service discontinuation.',
          ]} />
          <P>Nothing in these Terms limits liability that cannot be limited under applicable law, including liability for personal injury caused by our negligence.</P>
        </Section>

        <Section id="termination" title="13. Termination">
          <P>We reserve the right to suspend or permanently terminate your access to AssessMonk if:</P>
          <Ul items={[
            'You breach any provision of these Terms.',
            'We determine your usage poses a risk to the platform, other users, or third parties.',
            'The platform is discontinued or substantially modified.',
          ]} />
          <P>You may terminate your account at any time by contacting <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a>. Upon termination, you will lose access to your saved assessment history. For information on how your data is handled upon account closure, see our <Link to="/privacy" style={{ color: C.indigo, fontWeight: 600 }}>Privacy Policy</Link>.</P>
        </Section>

        <Section id="governing-law" title="14. Governing Law & Jurisdiction">
          <P>These Terms and Conditions are governed by and construed in accordance with the laws of India. Any dispute, claim, or legal matter arising out of or in connection with these Terms or the use of the AssessMonk platform shall be subject to the exclusive jurisdiction of the courts in Dehradun, Uttarakhand, India.</P>
          <P>If any provision of these Terms is found to be unenforceable under applicable law, that provision shall be modified to the minimum extent necessary to make it enforceable, and the remaining provisions shall continue in full force and effect.</P>
        </Section>

        <Section id="contact" title="15. Contact">
          <P>For questions about these Terms, platform use, or legal matters, please contact us:</P>
          <div style={{ background: C.indigoXL, borderRadius: 14, padding: '20px 24px', border: '1px solid #c7d2fe' }}>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: C.navy, marginBottom: 6 }}>AssessMonk</div>
            <div style={{ fontSize: '0.88rem', color: C.muted, marginBottom: 4 }}>Email: <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a></div>
            <div style={{ fontSize: '0.88rem', color: C.muted }}>Website: <a href="https://assessmonk.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk.com</a></div>
          </div>
        </Section>

      </div>

      <LegalFooter />
    </div>
  );
}
